import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Navigation, Building2, Camera, Heart, Wine, Utensils, Cake, Music, PartyPopper, Coffee, Sun } from 'lucide-react';
import { PublicLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { getConfig, getProgramme } from '@/lib/api';

// Map icon names to components
const ICON_MAP = {
  Building2, Camera, Heart, Wine, Utensils, Cake, Music, PartyPopper, Coffee, Sun
};

const TimelineEvent = ({ time, title, icon, side = 'left', delay = 0 }) => {
  const isLeft = side === 'left';
  const Icon = typeof icon === 'string' ? (ICON_MAP[icon] || PartyPopper) : icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: isLeft ? -20 : 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      className={`flex items-center gap-4 ${isLeft ? 'flex-row' : 'flex-row-reverse'} mb-6`}
    >
      <div className={`flex-1 ${isLeft ? 'text-right' : 'text-left'}`}>
        <span className="text-lg font-semibold text-charcoal">{time}</span>
        <p className="text-stone text-sm">{title}</p>
      </div>
      <div className="w-12 h-12 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0 border-2 border-terracotta/30">
        <Icon className="w-5 h-5 text-terracotta" />
      </div>
      <div className="flex-1"></div>
    </motion.div>
  );
};

const ProgrammePage = () => {
  const [config, setConfig] = useState(null);
  const [samediEvents, setSamediEvents] = useState([]);
  const [dimancheEvents, setDimancheEvents] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [configRes, programmeRes] = await Promise.all([
          getConfig(),
          getProgramme()
        ]);
        setConfig(configRes.data);
        
        // Add alternating sides
        const samedi = (programmeRes.data.samedi || []).map((e, i) => ({
          ...e,
          side: i % 2 === 0 ? 'left' : 'right'
        }));
        const dimanche = (programmeRes.data.dimanche || []).map((e, i) => ({
          ...e,
          side: i % 2 === 0 ? 'left' : 'right'
        }));
        
        setSamediEvents(samedi);
        setDimancheEvents(dimanche);
      } catch (error) {
        console.error('Error loading data:', error);
        // Fallback to default
        setSamediEvents([
          { time: '14h00', title: 'Union civile - Mairie de Sainte-Eulalie', icon: 'Building2', side: 'left' },
          { time: '15h00', title: 'Rdv au Domaine de la Rose Blanche', icon: 'Camera', side: 'right' },
          { time: '16h45', title: 'Cérémonie laïque', icon: 'Heart', side: 'left' },
          { time: '18h00', title: "Vin d'honneur & Cocktails", icon: 'Wine', side: 'right' },
          { time: '19h00', title: 'Lancé du bouquet', icon: 'PartyPopper', side: 'left' },
          { time: '21h00', title: 'Repas', icon: 'Utensils', side: 'right' },
          { time: '23h45', title: 'Ouverture du bal', icon: 'Music', side: 'left' },
          { time: '04h00', title: 'Fin de soirée en douceur', icon: 'Coffee', side: 'right' },
        ]);
        setDimancheEvents([
          { time: '11h00', title: 'Brunch', icon: 'Coffee', side: 'left' },
          { time: '16h00', title: 'Chill, piscine, pétanque...', icon: 'Sun', side: 'right' },
        ]);
      }
    };
    loadData();
  }, []);

  const openMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-terracotta-light/30 to-white overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-accent text-3xl md:text-4xl text-charcoal italic mb-2" data-testid="programme-title">
              Deux jours d'amour
            </h1>
            <p className="font-accent text-xl md:text-2xl text-terracotta italic mb-8">
              On vous attend pour tout vivre avec nous
            </p>
          </motion.div>
        </div>
      </section>

      {/* Samedi */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl md:text-4xl text-charcoal text-center mb-10"
          >
            SAMEDI
          </motion.h2>
          
          {/* Timeline central */}
          <div className="relative">
            {/* Ligne verticale centrale */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-terracotta/20 -translate-x-1/2"></div>
            
            {samediEvents.map((event, index) => (
              <TimelineEvent
                key={index}
                {...event}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Dimanche */}
      <section className="py-12 md:py-16 bg-bone">
        <div className="max-w-xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-3xl md:text-4xl text-charcoal text-center mb-10"
          >
            DIMANCHE
          </motion.h2>
          
          {/* Timeline central */}
          <div className="relative">
            {/* Ligne verticale centrale */}
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-terracotta/20 -translate-x-1/2"></div>
            
            {dimancheEvents.map((event, index) => (
              <TimelineEvent
                key={index}
                {...event}
                delay={index * 0.1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl text-charcoal mb-4">
              Les lieux
            </h2>
            <p className="text-stone">
              Retrouvez les adresses et itinéraires pour chaque étape.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Mairie Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-elegant"
            >
              <div className="aspect-video rounded-xl overflow-hidden mb-4">
                <iframe
                  title="Mairie de Sainte-Eulalie"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2827.5!2d-0.48!3d44.91!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDTCsDU0JzM2LjAiTiAwwrAyOCc0OC4wIlc!5e0!3m2!1sfr!2sfr!4v1"
                  className="w-full h-full border-0"
                  loading="lazy"
                  allowFullScreen
                ></iframe>
              </div>
              <h3 className="font-display text-xl text-charcoal mb-2">Mairie de Sainte-Eulalie</h3>
              <p className="text-stone text-sm mb-4">{config?.mairie_address || '1 Place Charles de Gaulle, 33560 Sainte-Eulalie'}</p>
              <Button
                data-testid="mairie-maps-btn"
                onClick={() => openMaps(config?.mairie_address || 'Mairie de Sainte-Eulalie, 33560')}
                className="btn-secondary w-full"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Ouvrir dans Maps
              </Button>
            </motion.div>

            {/* Domaine Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-elegant"
            >
              <div className="aspect-video rounded-xl overflow-hidden mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_a31999b5-4753-4cfc-9fe8-180dccde4d88/artifacts/t0zp5out_image.png"
                  alt="Domaine de la Rose Blanche"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-display text-xl text-charcoal mb-2">Domaine de la Rose Blanche</h3>
              <p className="text-stone text-sm mb-4">{config?.domaine_address || 'Domaine de la Rose Blanche, 40 Av. de Branne, 33370 Tresses'}</p>
              <Button
                data-testid="domaine-maps-btn"
                onClick={() => openMaps(config?.domaine_address || 'Domaine de la Rose Blanche, 33370 Tresses')}
                className="btn-secondary w-full"
              >
                <Navigation className="w-4 h-4 mr-2" />
                Ouvrir dans Maps
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ProgrammePage;
